/**
 * De zeven gebeurteniskaarten van het basisspel (de blauwe stapel).
 *
 * Regelteksten uit `assets/text/basic.txt`; ze staan hieronder per kaart geciteerd zodat
 * je de implementatie tegen de bron kunt houden.
 *
 * Vier kaarten werken vanzelf, drie vragen een keuze van een speler. Die laatste zetten
 * — net als bij de dobbelsteengebeurtenissen — een vraag klaar in `state.pending` en
 * wachten; de kern beslist nooit voor de speler.
 */
import { BASIC_CARDS, LANDSCAPE_RESOURCE, type Resource, type Seat } from '@catan/contracts';
import { shuffle, type Rng } from './rng.js';
import { pick } from './rng.js';
import type { GameState, LandscapeTile, PlayerState, Side } from './state.js';

const byId = new Map(BASIC_CARDS.map((c) => [c.id, c]));
const nameOf = (cardId: number): string => byId.get(cardId)?.name ?? '';

/** De zeven gebeurteniskaarten, op naam. */
export const EVENT_CARD_NAMES = [
  'Bouwmeester',
  'Burgeroorlog',
  'Conflict',
  'Jaarwisseling',
  'Goed jaar',
  'Vooruitgang',
  'Pest',
] as const;
export type EventCardName = (typeof EVENT_CARD_NAMES)[number];

export function eventCardName(cardId: number): EventCardName | undefined {
  const naam = nameOf(cardId);
  return (EVENT_CARD_NAMES as readonly string[]).includes(naam)
    ? (naam as EventCardName)
    : undefined;
}

// ---------------------------------------------------------------- hulpmiddelen

/** Alle uitbreidingen die bij een bouwwerk aan één zijde staan. */
function expansionsAt(player: PlayerState, column: number, side: Side): readonly number[] {
  return player.principality.buildings.find((b) => b.column === column)?.expansions[side] ?? [];
}

/**
 * De bouwwerken waar een landschap aan grenst, met de zijde erbij.
 * Een uitbreiding werkt alleen op de landschappen aan zíjn kant (fysieke regels:
 * "Expansion cards above a city only affect regions above that city").
 */
function neighboursOf(player: PlayerState, tile: LandscapeTile): { column: number; kind: string }[] {
  return player.principality.buildings
    .filter((b) => b.column === tile.column - 1 || b.column === tile.column + 1)
    .map((b) => ({ column: b.column, kind: b.kind }));
}

/**
 * De vijf verdubbelaars: welke uitbouwkaart verdubbelt welk landschap?
 *
 * "De opbrengsten van aangrenzende bergen verdubbelen zich" — dat is de hele functie van
 * deze kaarten, en ze deden bij ons niets. Ze werken net als het Pakhuis en het Badhuis:
 * op de landschappen náást het bouwwerk waar de kaart op ligt, aan diezelfde kant.
 */
const VERDUBBELAARS: Readonly<Record<string, string>> = {
  Graanmolen: 'akker',
  IJzergieterij: 'bergen',
  Steenoven: 'heuvels',
  Wolspinnerij: 'weide',
  Houtzagerij: 'bos',
};

/**
 * Hoeveel levert dit landschap op bij een treffer — één of twee?
 *
 * Twee als er een passende verdubbelaar naast ligt. Een tweede verdubbelaar van dezelfde
 * soort verdubbelt niet nog eens; de kaarttekst spreekt over "verdubbelen", niet over
 * stapelen.
 */
export function yieldOf(player: PlayerState, tile: LandscapeTile): number {
  const passend = Object.entries(VERDUBBELAARS)
    .filter(([, landschap]) => landschap === tile.landscape)
    .map(([kaart]) => kaart);
  if (passend.length === 0) return 1;
  return hasNeighbourCard(player, tile, passend) ? 2 : 1;
}

/** Grenst dit landschap aan een bouwwerk met zo'n uitbreiding aan dezelfde kant? */
function hasNeighbourCard(player: PlayerState, tile: LandscapeTile, namen: readonly string[]): boolean {
  return neighboursOf(player, tile).some((b) =>
    expansionsAt(player, b.column, tile.side).some((id) => namen.includes(nameOf(id))),
  );
}

function mapLandscapes(
  player: PlayerState,
  fn: (tile: LandscapeTile) => LandscapeTile,
): PlayerState {
  return {
    ...player,
    principality: { ...player.principality, landscapes: player.principality.landscapes.map(fn) },
  };
}

// ---------------------------------------------------------------- de vier automatische

/**
 * **Pest** — "Elk landschap, dat aan een stad grenst, verliest 1 grondstof.
 * Bescherming: badhuis of watervoorziening."
 */
export function applyPlague(player: PlayerState): { player: PlayerState; lost: number } {
  // De twee beschermingen werken op verschillende schaal, en dat verschil staat op de
  // kaarten zelf: het badhuis beschermt "alle 4 landschappen rondom **deze stad**", de
  // watervoorziening "**alle** landschappen van je vorstendom". Ze allebei als buurkaart
  // behandelen maakte de watervoorziening stilzwijgend een duur badhuis.
  const heeftWatervoorziening = builtIn(player).some((id) => nameOf(id) === 'Watervoorz.');
  if (heeftWatervoorziening) return { player, lost: 0 };

  let lost = 0;
  const na = mapLandscapes(player, (tile) => {
    if (tile.resources === 0) return tile;
    const bijStad = neighboursOf(player, tile).some((b) => b.kind === 'stad');
    if (!bijStad) return tile;
    if (hasNeighbourCard(player, tile, ['Badhuis'])) return tile;
    lost += 1;
    return { ...tile, resources: tile.resources - 1 };
  });
  return { player: na, lost };
}

/** Alles wat deze speler in zijn vorstendom heeft staan. */
function builtIn(player: PlayerState): number[] {
  return player.principality.buildings.flatMap((b) => [...b.expansions.boven, ...b.expansions.onder]);
}

/**
 * **Goed jaar** (de kaart, niet het dobbelsteenvlak) — "Elk landschap, dat aan een
 * pakhuis grenst, krijgt er een grondstof bij."
 */
export function applyGoodYear(player: PlayerState): { player: PlayerState; gained: number } {
  let gained = 0;
  const na = mapLandscapes(player, (tile) => {
    if (tile.resources >= 3) return tile;
    if (!hasNeighbourCard(player, tile, ['Pakhuis'])) return tile;
    gained += 1;
    return { ...tile, resources: tile.resources + 1 };
  });
  return { player: na, gained };
}

/**
 * **Vooruitgang** — "Iedere speler krijgt er voor elk klooster, elke bibliotheek en elke
 * universiteit een willekeurige grondstof bij."
 *
 * Willekeurig betekent hier: een willekeurig landschap dat nog ruimte heeft.
 */
export function applyProgress(
  player: PlayerState,
  rng: Rng,
): { player: PlayerState; rng: Rng; gained: number } {
  const gebouwen = player.principality.buildings
    .flatMap((b) => [...b.expansions.boven, ...b.expansions.onder])
    .filter((id) => ['Klooster', 'Bibliotheek', 'Universiteit'].includes(nameOf(id))).length;

  let current = player;
  let currentRng = rng;
  let gained = 0;
  for (let i = 0; i < gebouwen; i++) {
    const ruimte = current.principality.landscapes.filter((t) => t.resources < 3);
    if (ruimte.length === 0) break;
    const keuze = pick(currentRng, ruimte);
    currentRng = keuze.rng;
    current = mapLandscapes(current, (tile) =>
      tile.column === keuze.value.column && tile.side === keuze.value.side
        ? { ...tile, resources: tile.resources + 1 }
        : tile,
    );
    gained += 1;
  }
  return { player: current, rng: currentRng, gained };
}

/** **Jaarwisseling** — "De gebeurteniskaartenstapel wordt opnieuw geschud." */
export function applyNewYear(
  deck: readonly number[],
  rng: Rng,
): { deck: number[]; rng: Rng } {
  const geschud = shuffle(rng, deck);
  return { deck: geschud.value, rng: geschud.rng };
}

// ---------------------------------------------------------------- de drie met een keuze

/**
 * **Burgeroorlog** — "Iedere speler moet een ridderkaart of vloot terug in de hand
 * nemen. De tegenstander bepaalt welke kaart dat is. Bescherming: kerk."
 *
 * Beschermd is een speler die ergens een kerk heeft staan.
 */
export function isProtectedByChurch(player: PlayerState): boolean {
  return player.principality.buildings
    .flatMap((b) => [...b.expansions.boven, ...b.expansions.onder])
    .some((id) => nameOf(id) === 'Kerk');
}

/** De ridders en vloten die een speler gebouwd heeft — de kandidaten voor Burgeroorlog. */
export function knightsAndFleets(
  player: Pick<PlayerState, 'principality'>,
): { column: number; side: Side; cardId: number }[] {
  const uit: { column: number; side: Side; cardId: number }[] = [];
  for (const building of player.principality.buildings) {
    for (const side of ['boven', 'onder'] as const) {
      for (const cardId of building.expansions[side]) {
        const card = byId.get(cardId);
        if (card === undefined) continue;
        if (card.strength > 0 || card.name.endsWith('vloot')) {
          uit.push({ column: building.column, side, cardId });
        }
      }
    }
  }
  return uit;
}

/** Haalt een gebouwde kaart weg en geeft hem terug in de hand van de eigenaar. */
export function takeBackCard(
  player: PlayerState,
  column: number,
  side: Side,
  cardId: number,
): PlayerState {
  const buildings = player.principality.buildings.map((b) => {
    if (b.column !== column) return b;
    const rij = [...b.expansions[side]];
    const index = rij.indexOf(cardId);
    if (index === -1) return b;
    rij.splice(index, 1);
    return { ...b, expansions: { ...b.expansions, [side]: rij } };
  });
  return {
    ...player,
    principality: { ...player.principality, buildings },
    hand: [...player.hand, cardId],
  };
}

/** Schuift kaarten uit een hand onder een stapel (onderop, ongezien). */
export function tuckUnderStack(
  state: GameState,
  seat: Seat,
  cardIds: readonly number[],
  stack: number,
): GameState {
  const players = [...state.players] as [PlayerState, PlayerState];
  const hand = [...players[seat].hand];
  for (const cardId of cardIds) {
    const index = hand.indexOf(cardId);
    if (index !== -1) hand.splice(index, 1);
  }
  players[seat] = { ...players[seat], hand };
  const stacks = state.stacks.map((s, i) => (i === stack ? [...s, ...cardIds] : s));
  return { ...state, players, stacks };
}

/** Grondstofsoorten waarvan een speler er minstens één heeft. */
export function availableResources(player: PlayerState): Resource[] {
  const soorten = new Set<Resource>();
  for (const tile of player.principality.landscapes) {
    if (tile.resources > 0) soorten.add(LANDSCAPE_RESOURCE[tile.landscape]);
  }
  return [...soorten];
}
